/**
 * T8008 · TanStack `QueryClient` defaults — `refetchOnReconnect: false`.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1296 — fixes verified TanStack bug #8: `refetchOnReconnect: 'always'`
 * does NOT refetch on remount; it only fires when `online` lands WHILE
 * observers are mounted. Verified at
 * `/tmp/morfi-sdk-connectivity-proto/claim2.ts` (log out-claim2.log).
 *
 * The SDK overrides TanStack's default so the strategy-aware ladder
 * (T8006) + function-form `refetchOnMount` (T8007) handle reconnects
 * explicitly. Mutations default to `networkMode: 'online'`.
 *
 * IDEMPOTENT
 * ----------
 * `applySdkDefaults(qc)` may be called twice — the second call sets the
 * same options. TanStack's `setDefaultOptions` is itself idempotent for
 * identical inputs.
 */

import type { QueryClient } from '@tanstack/react-query';
import { assertNotServer } from '../connectivity/orchestration/server-guard.js';
import { retryDelayMs, retryPolicy } from '../errors/policy/retry-policy.js';
import { staleWhileErrorThrowOnError } from '../errors/policy/stale-while-error.js';
import { realtimeRefetchOnMount } from './refetch-on-mount.js';

export function applySdkDefaults(qc: QueryClient): void {
  assertNotServer();
  qc.setDefaultOptions({
    queries: {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      gcTime: 5 * 60_000,
      // FR12 fix #1 · stale-while-error: only schema/invariant errors
      // throw to the boundary; everything else stays in `query.error`
      // with the last-known data still rendered. Per-category retry
      // ladder replaces the blanket retry:3 (which used to storm 5xx).
      throwOnError: staleWhileErrorThrowOnError,
      retry: retryPolicy,
      retryDelay: retryDelayMs,
    },
    mutations: {
      networkMode: 'online',
      throwOnError: false,
      retry: retryPolicy,
      retryDelay: retryDelayMs,
    },
  });
}

export { realtimeRefetchOnMount };
